import {useQuery, UseQueryOptions} from '@tanstack/react-query';
import {type} from 'arktype';
import {AccountId} from '~/entities/operations-fiat';
import {expectEither, HttpClient, POST, ServerValidationResult, useHttpClient} from '~/library/network-facade';
import {Outcome} from '~/library/types';
import {useDebounce} from '~/library/ui-kit';

export const SepaBankInfoResponse = type({
    iban: 'string',
    bank: 'string',
    bban: 'string',
});
export type SepaBankInfoResponse = typeof SepaBankInfoResponse.infer;

const SepaBankInfoFields = type({
    iban: 'string',
});
type SepaBankInfoFields = typeof SepaBankInfoFields.infer;

function fetchSepaBankInfo(http: HttpClient, accountId: AccountId, iban: string) {
    return http(`v1/accounts/${accountId}/send/banks/sepa/search`, POST({iban})).then(
        ...expectEither({
            200: SepaBankInfoResponse,
            204: null,
            400: ServerValidationResult.to(SepaBankInfoFields),
        })
    );
}

function useSepaBankInfoOptions(
    accountId: AccountId,
    iban: string
): UseQueryOptions<Outcome<SepaBankInfoResponse | null, SepaBankInfoFields>> {
    const http = useHttpClient();

    return {
        queryKey: ['v1/accounts/{accountId}/send/banks/sepa/search', iban],
        queryFn: async ({client, queryKey}) => {
            const res = await fetchSepaBankInfo(http, accountId, iban);

            switch (res.status) {
                case 200:
                    return {value: res.json};
                case 204: {
                    return {value: null};
                }
                case 400:
                    return {problem: res.json};
                case 'Abort': {
                    client.cancelQueries({queryKey});
                    throw res.reason;
                }
            }
        },
    };
}

export function useSepaBankInfo(accountId: AccountId, iban: string) {
    const debouncedAccountId = useDebounce(200, accountId);
    const debouncedIban = useDebounce(200, iban);
    const queryOptions = useSepaBankInfoOptions(debouncedAccountId.value, debouncedIban.value);
    const query = useQuery(queryOptions);
    return {...query, isDebouncing: debouncedAccountId.isDebouncing || debouncedIban.isDebouncing};
}
